import React, { useState, useEffect } from "react";
import { getLeads, getStatuses, updateLead } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { ScrollArea, ScrollBar } from "../components/ui/scroll-area";
import { toast } from "sonner";
import { getInitials, cn } from "../lib/utils";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { useNavigate } from "react-router-dom";
import { GripHorizontal, Phone, Mail } from "lucide-react";

export const KanbanPage = () => {
  const [leads, setLeads] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draggingLead, setDraggingLead] = useState(null);
  const navigate = useNavigate();

  const fetchData = async () => {
    try {
      const [leadsRes, statusesRes] = await Promise.all([
        getLeads({ limit: 500 }),
        getStatuses(),
      ]);
      setLeads(leadsRes.data);
      setStatuses(statusesRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getLeadsByStatus = (statusId) => {
    return leads.filter((lead) => lead.statusId === statusId);
  };

  const handleDragStart = (e, lead) => {
    setDraggingLead(lead);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = async (e, statusId) => {
    e.preventDefault();
    if (!draggingLead || draggingLead.statusId === statusId) {
      setDraggingLead(null);
      return;
    }

    try {
      await updateLead(draggingLead.id, { statusId });
      toast.success("Statut mis à jour");
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    } finally {
      setDraggingLead(null);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="kanban-loading">
        <Skeleton className="h-8 w-48" />
        <div className="flex gap-4 overflow-hidden">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-[600px] w-80 flex-shrink-0" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 h-full" data-testid="kanban-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Pipeline</h1>
        <p className="text-muted-foreground mt-1">
          Glissez-déposez les leads pour changer leur statut
        </p>
      </div>

      {/* Kanban Board */}
      <ScrollArea className="w-full">
        <div className="flex gap-4 pb-4 min-w-max">
          {statuses.map((status) => {
            const statusLeads = getLeadsByStatus(status.id);
            return (
              <div
                key={status.id}
                className="w-80 flex-shrink-0"
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, status.id)}
              >
                <Card
                  className={cn(
                    "h-[calc(100vh-220px)] flex flex-col",
                    draggingLead && draggingLead.statusId !== status.id && "ring-2 ring-primary/50"
                  )}
                >
                  <CardHeader className="pb-3 flex-shrink-0">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: status.color }}
                        />
                        <CardTitle className="text-sm font-semibold">
                          {status.name}
                        </CardTitle>
                      </div>
                      <Badge variant="secondary" className="text-xs">
                        {statusLeads.length}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="flex-1 overflow-hidden p-2">
                    <ScrollArea className="h-full">
                      <div className="space-y-2 pr-2">
                        {statusLeads.length === 0 ? (
                          <div className="h-24 border-2 border-dashed border-border rounded-lg flex items-center justify-center text-muted-foreground text-sm">
                            Aucun lead
                          </div>
                        ) : (
                          statusLeads.map((lead) => (
                            <div
                              key={lead.id}
                              draggable
                              onDragStart={(e) => handleDragStart(e, lead)}
                              onClick={() => navigate(`/leads/${lead.id}`)}
                              className={cn(
                                "p-3 bg-card border rounded-lg cursor-grab active:cursor-grabbing hover:border-primary/50 transition-colors group",
                                draggingLead?.id === lead.id && "opacity-50"
                              )}
                              data-testid={`kanban-card-${lead.id}`}
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex items-center gap-2">
                                  <Avatar className="h-8 w-8">
                                    <AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">
                                      {getInitials(`${lead.firstName} ${lead.lastName}`)}
                                    </AvatarFallback>
                                  </Avatar>
                                  <div>
                                    <p className="font-medium text-sm">
                                      {lead.firstName} {lead.lastName}
                                    </p>
                                    {lead.assignedSellerName && (
                                      <p className="text-xs text-muted-foreground">
                                        {lead.assignedSellerName}
                                      </p>
                                    )}
                                  </div>
                                </div>
                                <GripHorizontal className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                              </div>
                              
                              <div className="mt-2 space-y-1">
                                {lead.email && (
                                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                    <Mail className="h-3 w-3" />
                                    <span className="truncate">{lead.email}</span>
                                  </div>
                                )}
                                {lead.phone && (
                                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                    <Phone className="h-3 w-3" />
                                    {lead.phone}
                                  </div>
                                )}
                              </div>

                              {lead.noteCount > 0 && (
                                <Badge variant="outline" className="mt-2 text-xs">
                                  {lead.noteCount} note{lead.noteCount > 1 ? "s" : ""}
                                </Badge>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>
              </div>
            );
          })}
        </div>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
    </div>
  );
};
